import { useState, useRef, useEffect, useMemo, useContext } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { IoPeople, IoMail, IoChevronDown, IoCheckmark, IoClose } from 'react-icons/io5';
import 'bootstrap/dist/css/bootstrap.min.css';
import { accessModalStyle } from '../../../../common';
import { FaLock } from 'react-icons/fa';
import { shareSheetAccess } from '../../../../../api/businessAndEnterprise/sheetApi';
import ReactNotifications, { showApiErrors } from '../../../../common/reactNotifications/ReactNotifications';
import * as utilConstants from '../../../../../utils/constants/utilsConstants';
import { fetchEmployees } from '../../../../../api/companyStructureApi/companyStructureApi';
import Context from '../../../../../context/Context';
import { ManageAccessTab } from './ManageAccessTab';
import { MdManageAccounts } from 'react-icons/md';
import { LuUsers } from "react-icons/lu";



const SHARE_COLOR = '#AEF468';
const ShareSheetAccessModal = ({ id }) => {
    const [show, setShow] = useState(false);
    const [activeTab, setActiveTab] = useState('internal');
    const [showDropdown, setShowDropdown] = useState(false);
    const [internalUsers, setInternalUsers] = useState([]);
    const dropdownRef = useRef(null);
    const { user } = useContext(Context);

    const [selectedInternalUsers, setSelectedInternalUsers] = useState([]);
    const [internalAccessLevel, setInternalAccessLevel] = useState('view');
    const [internalUsersError, setInternalUsersError] = useState('');

    const [externalEmails, setExternalEmails] = useState('');
    const [externalAccessLevel, setExternalAccessLevel] = useState('view');
    const [validityDays, setValidityDays] = useState(30);
    const [externalEmailsError, setExternalEmailsError] = useState('');

    const expiryOptions = [
        { value: 10, label: '10 Days' },
        { value: 20, label: '20 Days' },
        { value: 30, label: '30 Days' },
        { value: 40, label: '40 Days' },
        { value: 50, label: '50 Days' },
        { value: 60, label: '60 Days' },
        { value: 70, label: '70 Days' },
        { value: 80, label: '80 Days' },
        { value: 90, label: '90 Days' },

    ];


    const filteredInternalUsers = useMemo(() => {
        return (
            internalUsers?.filter((employee) => {
                const isCurrentUser = employee?.user_id === user.id;
                const isMemberWithoutTeam =
                    employee?.role === "Member" && employee?.team_name === null;
                return !isCurrentUser && !isMemberWithoutTeam;
            }) || []
        );
    }, [internalUsers, user.id]);


    useEffect(() => {
        fetchEmployees({ page: false }, (response) => {
            if (response?.status === true && response?.data?.length > 0) {
                setInternalUsers(response?.data);
            }
        });
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleShow = () => setShow(true);
    const handleClose = () => {
        setShow(false);
        setActiveTab('internal');
        setSelectedInternalUsers([]);
        setInternalAccessLevel('view');
        setExternalEmails('');
        setExternalAccessLevel('view');
        setValidityDays(7);
        setShowDropdown(false);
        setInternalUsersError('');
        setExternalEmailsError('');
    };

    const toggleInternalUser = (email) => {
        if (internalUsersError) setInternalUsersError('');

        setSelectedInternalUsers(prev =>
            prev.includes(email)
                ? prev.filter(e => e !== email)
                : [...prev, email]
        );
    };

    const getSelectedUserNames = () => {
        if (selectedInternalUsers.length === 0) return 'Select users...';
        if (selectedInternalUsers.length === 1) {
            return `${selectedInternalUsers.length} user selected`;
        }
        return `${selectedInternalUsers.length} users selected`;
    };

    const isValidEmail = (email) => {
        return /\S+@\S+\.\S+/.test(email);
    };

    const handleShare = () => {
        setInternalUsersError('');
        setExternalEmailsError('');

        let actualPayload = {};

        if (activeTab === 'internal') {
            const emailList = selectedInternalUsers;

            if (emailList.length === 0) {
                setInternalUsersError('Please select at least one internal user');
                return;
            }

            const internalPayload = {
                invitations: emailList.map(email => ({
                    email,
                    access_level: internalAccessLevel
                }))
            };
            actualPayload = internalPayload;

        } else {
            const rawEmails = externalEmails
                .split(/[\n,]+/)
                .map(e => e.trim())
                .filter(e => e);

            const invalidEmails = rawEmails.filter(email => !isValidEmail(email));
            const validEmails = rawEmails.filter(email => isValidEmail(email));

            if (validEmails.length === 0) {
                setExternalEmailsError('Please enter at least one valid email address');
                return;
            }

            if (invalidEmails.length > 0) {
                ReactNotifications(`The following emails are invalid and were ignored: ${invalidEmails.join(', ')}`, utilConstants.getErrorStatus);
            }

            const externalPayload = {
                invitations: validEmails.map(email => ({
                    email,
                    access_level: externalAccessLevel,
                    visibility: 'external',
                    validity_days: validityDays
                }))
            };

            actualPayload = externalPayload;
        }

        shareSheetAccess(id, actualPayload, (res) => {
            if (res?.status === true || res?.status === 200) {
                ReactNotifications(res?.message || res?.data?.message, utilConstants.sucessState200);
                handleClose();
            } else {
                showApiErrors(res);
            }
        });
    };

    return (
        <div className="flex items-center justify-center">
            <style>{accessModalStyle}</style>

            <button
                onClick={handleShow}
                className="flex items-center !border-2 !border-gray-800 px-2 sm:px-3 py-1 bg-white hover:bg-gray-50 rounded-lg shadow-lg cursor-pointer transition-all duration-200 hover:shadow-xl"
                style={{ backgroundColor: SHARE_COLOR }}
                onMouseEnter={(e) => {
                    const tooltip = e.currentTarget.querySelector('.lock-tooltip');
                    if (tooltip) tooltip.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                    const tooltip = e.currentTarget.querySelector('.lock-tooltip');
                    if (tooltip) tooltip.style.opacity = '0';
                }}
            >
                <LuUsers size={15} className="text-gray-700 sm:w-[20px] sm:h-[20px]" />
                <div
                    className="lock-tooltip z-[10000] absolute top-full ml-2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap pointer-events-none transition-opacity duration-200"
                    style={{
                        transform: 'translateX(-50%)',
                        opacity: 0,
                    }}
                >
                    Share
                    <div
                        className="absolute bottom-full left-1/2 border-4 border-transparent border-b-gray-900"
                        style={{ transform: 'translateX(-50%)', marginBottom: '-8px' }}
                    ></div>
                </div>
            </button>

            <Modal show={show} onHide={handleClose} centered size="lg">
                <Modal.Header>
                    <div className="w-full">
                        <div className="flex justify-between items-start">
                            <div>
                                <Modal.Title className="text-xl font-bold mb-1">Share Sheet Access</Modal.Title>
                                <p className="text-gray-500 text-sm mb-0">Grant access to users</p>
                            </div>
                            <button
                                onClick={handleClose}
                                className="!mr-[-15px] !mt-[-2px] p-2 text-gray-400 hover:text-gray-600 transition"
                                aria-label="Close"
                            >
                                <IoClose size={29} />
                            </button>
                        </div>

                        <div className="flex gap-2 mt-4 border-b border-gray-200">
                            <button
                                onClick={() => setActiveTab('internal')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold transition-all duration-200 border-b-2 ${activeTab === 'internal'
                                    ? 'bg-white text-black border-lime-500 shadow-sm'
                                    : 'bg-gray-100 text-gray-600 border-transparent hover:bg-gray-200'
                                    }`}
                                style={activeTab === 'internal' ? { borderBottomColor: SHARE_COLOR } : {}}
                            >
                                <IoPeople size={18} />
                                Internal Users
                            </button>
                            <button
                                onClick={() => setActiveTab('external')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold transition-all duration-200 border-b-2 ${activeTab === 'external'
                                    ? 'bg-white text-black border-lime-500 shadow-sm'
                                    : 'bg-gray-100 text-gray-600 border-transparent hover:bg-gray-200'
                                    }`}
                                style={activeTab === 'external' ? { borderBottomColor: SHARE_COLOR } : {}}
                            >
                                <IoMail size={18} />
                                External Users
                            </button>
                            <button
                                onClick={() => setActiveTab('manage')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold transition-all duration-200 border-b-2 ${activeTab === 'manage'
                                    ? 'bg-white text-black border-lime-500 shadow-sm'
                                    : 'bg-gray-100 text-gray-600 border-transparent hover:bg-gray-200'
                                    }`}
                                style={activeTab === 'manage' ? { borderBottomColor: SHARE_COLOR } : {}}
                            >
                                <MdManageAccounts size={18} />
                                Manage Access
                            </button>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    {activeTab === 'internal' && (
                        <div>
                            <div className="mb-6">
                                <label className="block text-sm font-semibold mb-2">Select Users</label>

                                <div className="relative" ref={dropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => setShowDropdown(!showDropdown)}
                                        className={`w-full px-4 py-3 border-2 rounded-lg bg-white text-left text-gray-800 flex items-center justify-between cursor-pointer transition-colors duration-200 ${showDropdown ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                        style={{ borderColor: internalUsersError ? 'red' : (showDropdown ? '#6b7280' : '#d1d5db') }} // Highlight border on error
                                    >
                                        <span className={selectedInternalUsers.length === 0 ? 'text-gray-500' : 'text-gray-800'}>
                                            {getSelectedUserNames()}
                                        </span>
                                        <IoChevronDown
                                            size={20}
                                            className={`text-gray-500 transition-transform duration-200 ${showDropdown ? 'rotate-180' : 'rotate-0'}`}
                                        />
                                    </button>

                                    {internalUsersError && (
                                        <p className="text-red-500 text-sm mt-1 font-medium">{internalUsersError}</p>
                                    )}

                                    {showDropdown && (
                                        <div className="fixed bg-white border-2 rounded-lg shadow-xl max-h-64 overflow-y-auto member-dropdown-list z-50"
                                            style={{
                                                top: dropdownRef.current?.getBoundingClientRect().bottom + window.scrollY + 4,
                                                left: dropdownRef.current?.getBoundingClientRect().left + window.scrollX,
                                                width: dropdownRef.current?.getBoundingClientRect().width
                                            }}
                                        >
                                            {filteredInternalUsers?.map(user => (
                                                <label
                                                    key={user?.id}
                                                    className="px-3 sm:px-4 py-2.5 sm:py-3 cursor-pointer hover:bg-gray-100 border-b border-gray-200 transition-colors flex items-center gap-3 text-sm font-semibold text-gray-700"
                                                >
                                                    <div className="flex-shrink-0 relative">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedInternalUsers?.includes(user?.email)}
                                                            onChange={() => toggleInternalUser(user?.email)}
                                                            className="w-5 h-5 border-2 border-gray-300 rounded-md appearance-none cursor-pointer checked:bg-lime-500 checked:border-lime-500 transition-colors duration-150"
                                                            style={{ backgroundColor: selectedInternalUsers.includes(user?.email) ? SHARE_COLOR : 'white', borderColor: selectedInternalUsers.includes(user.email) ? SHARE_COLOR : '#d1d5db' }}
                                                        />
                                                        {selectedInternalUsers?.includes(user?.email) && (
                                                            <IoCheckmark
                                                                size={16}
                                                                className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-black pointer-events-none"
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="flex-grow">
                                                        <div className="font-medium text-gray-800">{user?.first_name + " " + user?.last_name}</div>
                                                        <div className="text-gray-500 text-sm">{user?.email}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {selectedInternalUsers.length > 0 && (
                                    <div className="mt-2 text-sm font-medium text-lime-600">
                                        <strong className='text-black'>Selected:</strong> {selectedInternalUsers.length} user{selectedInternalUsers.length > 1 ? 's' : ''}
                                    </div>
                                )}
                            </div>

                            <div className="mt-6">
                                <label className="block text-sm font-semibold mb-2">
                                    Access Level (applies to all selected users)
                                </label>
                                <div className="flex flex-col sm:flex-row gap-4 bg-gray-100 p-4 rounded-lg">
                                    <div className="flex items-center flex-1">
                                        <input
                                            type="radio"
                                            name="internal-access"
                                            id="internal-view"
                                            value="view"
                                            checked={internalAccessLevel === 'view'}
                                            onChange={(e) => setInternalAccessLevel(e.target.value)}
                                            className="form-radio h-5 w-5 text-lime-500 border-gray-300 focus:ring-lime-500"
                                            style={{ accentColor: SHARE_COLOR }}
                                        />
                                        <label className="ml-2 text-sm font-medium text-gray-700" htmlFor="internal-view">
                                            View Only
                                        </label>
                                    </div>
                                    <div className="flex items-center flex-1">
                                        <input
                                            type="radio"
                                            name="internal-access"
                                            id="internal-edit"
                                            value="edit"
                                            checked={internalAccessLevel === 'edit'}
                                            onChange={(e) => setInternalAccessLevel(e.target.value)}
                                            className="form-radio h-5 w-5 text-lime-500 border-gray-300 focus:ring-lime-500"
                                            style={{ accentColor: SHARE_COLOR }}
                                        />
                                        <label className="ml-2 text-sm font-medium text-gray-700" htmlFor="internal-edit">
                                            Can Edit
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'external' && (
                        <div>
                            <div className="mb-6">
                                <label className="block text-sm font-semibold mb-2">Add People</label>
                                <textarea
                                    value={externalEmails}
                                    onChange={(e) => {
                                        setExternalEmails(e.target.value);
                                        if (externalEmailsError) setExternalEmailsError('');
                                    }}
                                    placeholder="Enter email addresses (separate by comma or new line)&#10;e.g., user1@example.com, user2@example.com"
                                    rows={4}
                                    className={`w-full p-3 border rounded-lg focus:ring-1 focus:ring-lime-500 transition-colors duration-200 resize-none ${externalEmailsError ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-lime-500'}`}
                                    style={{
                                        borderColor: externalEmailsError ? 'red' : 'rgb(209 213 219)',
                                        '--tw-ring-color': SHARE_COLOR
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = externalEmailsError ? 'red' : SHARE_COLOR}
                                    onBlur={(e) => e.target.style.borderColor = externalEmailsError ? 'red' : 'rgb(209 213 219)'}
                                />
                                {externalEmailsError && (
                                    <p className="text-red-500 text-sm mt-1 font-medium">{externalEmailsError}</p>
                                )}
                                <small className="text-gray-500 d-block mt-2 text-sm">
                                    Tip: You can paste multiple emails separated by commas or line breaks
                                </small>
                            </div>

                            <div className="mt-6">
                                <label className="block text-sm font-semibold mb-2">
                                    Access Level (applies to all emails)
                                </label>
                                <div className="flex flex-col sm:flex-row gap-4 bg-gray-100 p-4 rounded-lg">
                                    <div className="flex items-center flex-1">
                                        <input
                                            type="radio"
                                            name="external-access"
                                            id="external-view"
                                            value="view"
                                            checked={externalAccessLevel === 'view'}
                                            onChange={(e) => setExternalAccessLevel(e.target.value)}
                                            className="form-radio h-5 w-5 text-lime-500 border-gray-300 focus:ring-lime-500"
                                            style={{ accentColor: SHARE_COLOR }}
                                        />
                                        <label className="ml-2 text-sm font-medium text-gray-700" htmlFor="external-view">
                                            View Only
                                        </label>
                                    </div>
                                    <div className="flex items-center flex-1">
                                        <input
                                            type="radio"
                                            name="external-access"
                                            id="external-edit"
                                            value="edit"
                                            checked={externalAccessLevel === 'edit'}
                                            onChange={(e) => setExternalAccessLevel(e.target.value)}
                                            className="form-radio h-5 w-5 text-lime-500 border-gray-300 focus:ring-lime-500"
                                            style={{ accentColor: SHARE_COLOR }}
                                        />
                                        <label className="ml-2 text-sm font-medium text-gray-700" htmlFor="external-edit">
                                            Can Edit
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6">
                                <label className="block text-sm font-semibold mb-2">Access Expiry</label>
                                <select
                                    value={validityDays}
                                    onChange={(e) => setValidityDays(parseInt(e.target.value))}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-lime-500 focus:ring-1 focus:ring-lime-500 transition-colors duration-200"
                                    style={{
                                        borderColor: 'rgb(209 213 219)',
                                        '--tw-ring-color': SHARE_COLOR
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = SHARE_COLOR}
                                    onBlur={(e) => e.target.style.borderColor = 'rgb(209 213 219)'}
                                >
                                    {expiryOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {activeTab === 'manage' && (
                        <ManageAccessTab sheetId={id} />
                    )}
                </Modal.Body>

                {activeTab !== 'manage' && (
                    <Modal.Footer className="flex justify-end gap-2 bg-gray-50 p-4 border-t border-gray-200 rounded-b-xl">
                        <Button
                            variant="primary"
                            onClick={handleShare}
                            className="px-6 py-2.5 text-black font-semibold !border-none rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                            style={{ backgroundColor: SHARE_COLOR }}
                        >
                            Share Access
                        </Button>
                    </Modal.Footer>
                )}
            </Modal>
        </div>
    );
};

export default ShareSheetAccessModal;